import { FC, MouseEvent, useState } from "react";
import Song from "../models/song";
import { Box, Button, DialogActions, DialogContent, Stack, Typography } from "@mui/material";
import useModal from "../hooks/useModal";
import { getPublicSongImageUrl } from "../utils/fileUtils";
import SongPlayerCompact from "./songPlayerCompact";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useS3 from "../hooks/useS3";
import AttributionLanguage from "./attributionLanguage";
import posthog from "posthog-js";
import { useLicenses } from "../contexts/licenseContext";

type Props = {
	song: Song;
};

const SongDialog: FC<Props> = ({ song }) => {
	const { hide: hideModal } = useModal();
	const s3 = useS3();
	const { addSongToCart, isSongInCart } = useLicenses();

	const [isDownloading, setIsDownloading] = useState(false);

	const handleDownloadMp3 = (event: MouseEvent) => {
		event.stopPropagation();

		try {
			setIsDownloading(true);
			s3.getDownloadUrl(song.mp3FileS3Key)
				.then(resp => {
					const url = resp.url;

					const link = document.createElement('a');
					link.href = url;
					link.download = song.mp3FileS3Key.split('/').pop() || 'download';
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					setIsDownloading(false);

					posthog.capture('song_downloaded', {
						songId: song.id,
						songTitle: song.title,
						songAlbum: song.album?.name || '',
						origin: 'song_dialog'
					});
				})
				.catch(err => {
					// toast.error(err.message)
					console.log(err.message);
					setIsDownloading(false);
				});
		}
		catch (err: any) {
			console.log('Error downloading s3 file:', err);
			setIsDownloading(false);
			// toast.error(err);
		}
	};

	const handleClose = () => {
		hideModal();
	}

	return (
		<>
			<DialogContent>
				<Stack direction={{ xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row' }} spacing={2}>
					<Stack spacing={2}>
						<Box
							component="img"
							src={getPublicSongImageUrl(song)}
							width={{ xs: '100%', sm: '100%', md: 200, lg: 250, xl: 250 }}
							sx={{
								height: { xs: 80, sm: 100, md: 200, lg: 250, xl: 250 },
								borderRadius: 0.5,
								objectFit: { xs: 'cover', sm: 'cover', md: 'initial', lg: 'initial', xl: 'initial' },
								objectPosition: '50% 50%'
							}}
						/>
					</Stack>
					<Stack spacing={2} flex="1 1 auto">
						<SongPlayerCompact song={song} />
						<Box sx={{ px: { xs: 0, sm: 0, md: 2, lg: 2, xl: 2 } }}>
							<Stack spacing={2}>
								<Typography sx={{ typography: { xs: 'body1', sm: 'body1', md: 'h6', lg: 'h6', xl: 'h6' }}}>Free Download with Attribution</Typography>
								<Button
									variant="contained"
									startIcon={<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'download' }} />}
									onClick={handleDownloadMp3}
									disabled={isDownloading}
								>
									Download
								</Button>
								<Button
									variant="outlined"
									startIcon={isSongInCart(song.id) ? <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'check' }} /> :<FontAwesomeIcon icon={{ prefix: 'far', iconName: 'plus-circle' }} />}
									onClick={() => addSongToCart(song)}
									disabled={isSongInCart(song.id)}
								>
									{isSongInCart(song.id) ? <span>Zero-Attribution License Added to Cart</span> : <span>Purchase Zero-Attribution License</span>}
								</Button>
								<Typography sx={{ typography: { xs: 'caption', sm: 'caption', md: 'body1', lg: 'body1', xl: 'body1' } }}>
									Please provide attribution using the following format:
								</Typography>
							</Stack>
						</Box>
						<AttributionLanguage song={song} />
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					color="primary"
					onClick={handleClose}
				>
					Close
				</Button>
			</DialogActions>
		</>
	);
};

export default SongDialog;